import { useState } from "react";
import {
  startOfMonth, endOfMonth, startOfYear, endOfYear, subDays, subMonths, format,
} from "date-fns";
import { it } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const presets = () => {
  const today = new Date();
  return [
    { id: "oggi", label: "Oggi", range: { from: today, to: today } },
    { id: "7g", label: "Ultimi 7 giorni", range: { from: subDays(today, 6), to: today } },
    { id: "30g", label: "Ultimi 30 giorni", range: { from: subDays(today, 29), to: today } },
    { id: "mese", label: "Questo mese", range: { from: startOfMonth(today), to: endOfMonth(today) } },
    { id: "mese-scorso", label: "Mese scorso", range: { from: startOfMonth(subMonths(today, 1)), to: endOfMonth(subMonths(today, 1)) } },
    { id: "anno", label: "Quest'anno", range: { from: startOfYear(today), to: endOfYear(today) } },
    { id: "tutto", label: "Tutto il periodo", range: null },
  ];
};

const labelFor = (range) => {
  if (!range || !range.from || !range.to) return "Tutto il periodo";
  const f = format(range.from, "dd MMM yyyy", { locale: it });
  const t = format(range.to, "dd MMM yyyy", { locale: it });
  return f === t ? f : `${f} – ${t}`;
};

export default function DateRangeFilter({ range, onChange, testid = "date-range-filter" }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(range);

  const apply = (r) => {
    onChange(r);
    setDraft(r);
    setOpen(false);
  };

  const active = !!(range && range.from && range.to);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(range); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={active ? "border-primary/50 text-foreground" : "text-muted-foreground"}
          data-testid={`${testid}-trigger`}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {labelFor(range)}
          {active && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); apply(null); }}
              className="ml-2 rounded p-0.5 hover:bg-accent"
              data-testid={`${testid}-clear`}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end" data-testid={`${testid}-content`}>
        <div className="flex flex-col sm:flex-row">
          <div className="flex flex-row flex-wrap gap-1 border-b p-2 sm:w-44 sm:flex-col sm:border-b-0 sm:border-r">
            {presets().map((p) => (
              <button
                key={p.id}
                onClick={() => apply(p.range)}
                data-testid={`${testid}-preset-${p.id}`}
                className="rounded-md px-3 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="p-2">
            <Calendar
              mode="range"
              locale={it}
              selected={draft}
              onSelect={setDraft}
              numberOfMonths={1}
              defaultMonth={range?.from || new Date()}
            />
            <div className="flex justify-end gap-2 border-t p-2">
              <Button variant="ghost" size="sm" onClick={() => apply(null)} data-testid={`${testid}-reset`}>
                Azzera
              </Button>
              <Button
                size="sm"
                disabled={!draft?.from || !draft?.to}
                onClick={() => apply(draft)}
                data-testid={`${testid}-apply`}
              >
                Applica
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
